import * as Sentry from "@sentry/browser";
import { ENV } from "../../config/loadShellEnv";
import { getPortalConfig } from "../../config/portalConfig";
import { safeRetrieveValue } from "../../helpers/safeRetrieveValue";
import { getBrowserLang } from "../../intl/intl.utils";
import { getUserType } from "../../portals/dashboard/utils/getUserType";
import { getCountryCode } from "../../setCountryConfig";
import { getRegionByCountryCode } from "../regionCountryCode";
import { getRudderStackAnonymousId } from "../rudderstack";
import { EVENT_NAME } from "../rudderstack/createRudderstackEvent";
import type { EventShellData, TrackEventPayload } from "../types";
import { getVendorId } from "../vendorId";
import { type GA4Event } from "./eventTypes";

const RS_TO_GA4_EVENT_MAP = {
  [EVENT_NAME.PAGE_LOADED]: "page_view",
};

const getAppName = () => {
  const configName = getPortalConfig("configName");
  if (!configName) {
    return null;
  }

  const [entry] = configName.split(".");

  if (!entry) {
    return null;
  }

  switch (entry) {
    case "mm":
      return "Engage";
    case "engage":
      return "Legacy Engage";
    case "cop":
      return "COP";
    case "dashboard":
      return "Build";

    default:
      return null;
  }
};

const GA4Brands = {
  engage: "sinch engage",
  build: "sinch build",
  mm: "messagemedia",
  messagemedia: "messagemedia",
  sincheu: "sinch engage",
  // For some reasons getVendorId() returns dashboard for sinch engage...
  dashboard: "sinch engage",
};

const toGA4Brand = (brand: string) =>
  GA4Brands[brand.toLowerCase() as keyof typeof GA4Brands] || brand;

export const createGA4Event = async (
  payload: TrackEventPayload,
  shellData: EventShellData
): Promise<GA4Event> => {
  const customInfo = payload.customInfo || payload.specificInfo;
  const { user } = shellData;

  const countryCode = await getCountryCode();
  if (!countryCode) {
    Sentry.captureException(new Error("Country code not found"), {
      tags: {
        operation: "create_rudderstack_client_event",
      },
    });
  }

  const userType = user?.email ? getUserType(user.email) : null;

  const eventName = payload.name;

  const brand = toGA4Brand(
    user?.app?.vendorId || getVendorId() || getAppName() || "unknown"
  );

  const businessId = safeRetrieveValue(
    user?.settings || {},
    "businessId",
    (x): x is string | null => x === null || typeof x === "string"
  );

  return valuesToLowerCase({
    event:
      eventName in RS_TO_GA4_EVENT_MAP
        ? RS_TO_GA4_EVENT_MAP[eventName as keyof typeof RS_TO_GA4_EVENT_MAP]
        : eventName,
    event_info: {
      brand_interaction: brand,
      product_interaction: "messaging",
      ...customInfo,
    },
    page_info: {
      page_url: location.href,
      page_path: location.pathname,
      page_title: document.title,
      page_referrer: document.referrer,
      // TODO: should use navigation state somehow — skipped for POC
      // page_type: payload.appName,
      portal: toGA4Brand(getAppName() || "unknown"),
      platform: "app",
      language: user.language ?? getBrowserLang() ?? "",
      logged_in: !!user.email,
      business_country: user.account?.country ?? countryCode ?? "unknown",
      // check based on account's region, then user's region, then portal config, then unknown
      business_region:
        getRegionByCountryCode(user.account?.country ?? countryCode ?? "") ??
        getPortalConfig("region") ??
        "unknown",
      environment: ENV ?? "unknown",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    user_info: {
      internal_traffic:
        user.isSuperUser || userType === "S" || userType === "L",
      user_type: businessId ? "partner" : "direct",
      user_id: user.id ?? "unknown",
      rs_anonymous_id: getRudderStackAnonymousId() ?? "unknown",
    },
  });
};

const valuesToLowerCase = <T extends Record<string, unknown>>(obj: T): T =>
  Object.entries(obj).reduce<T>((acc, [key, value]) => {
    if (typeof value === "string") {
      acc[key as keyof T] = value.toLowerCase() as T[keyof T];
    } else if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      acc[key as keyof T] = valuesToLowerCase(
        value as Record<string, unknown>
      ) as T[keyof T];
    } else if (Array.isArray(value)) {
      acc[key as keyof T] = value.map((item) =>
        typeof item === "string"
          ? item.toLowerCase()
          : item !== null && typeof item === "object" && !Array.isArray(item)
          ? valuesToLowerCase(item as Record<string, unknown>)
          : item
      ) as T[keyof T];
    } else {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {} as T);
